import React, { useEffect, useState, useRef } from 'react';
import { auth } from '../services/firebaseConfig';

// Global flag to prevent multiple initializations
if (typeof window !== 'undefined') {
  window.__CAST_API_LOADING = false;
}

const Cast = ({ sessionId, onCastStatusChange }, ref) => {
  // eslint-disable-next-line no-unused-vars
  const [isCastAvailable, setIsCastAvailable] = useState(false);
  const [isCastConnected, setIsCastConnected] = useState(false);
  const [castSession, setCastSession] = useState(null);
  const castButtonRef = useRef(null);

  useEffect(() => {
    // Check if already loading or loaded
    if (window.__CAST_API_LOADING) {
      console.log('Cast API is already loading...');
      return;
    }

    const loadCastSDK = () => {
      // Set loading flag
      window.__CAST_API_LOADING = true;
      console.log('Loading Cast SDK...');

      // Only set up callback if it doesn't exist
      if (!window['__onGCastApiAvailable']) {
        console.log('Setting up Cast API callback');
        window['__onGCastApiAvailable'] = function(isAvailable) {
          console.log('Cast API callback triggered:', isAvailable);
          if (isAvailable && window.cast && window.cast.framework) {
            try {
              initializeCastPlayer();
              setIsCastAvailable(true);
              
              // Create cast button after framework is initialized
              requestAnimationFrame(() => {
                if (castButtonRef.current) {
                  console.log('Creating Cast button');
                  const castButton = document.createElement('google-cast-launcher');
                  castButton.setAttribute('id', 'cast-button');
                  castButtonRef.current.appendChild(castButton);
                }
              });
            } catch (error) {
              console.error('Error in Cast API callback:', error);
            }
          } else {
            console.error('Cast API is not available');
          }
        };
      }

      // Only add script if it doesn't exist
      if (!document.getElementById('cast-api-script')) {
        const script = document.createElement('script');
        script.id = 'cast-api-script';
        script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
        script.async = true;
        document.head.appendChild(script);
      }
    };

    // Check if Chrome Cast is available
    if (!window.chrome) {
      console.warn('Chrome cast is not available - browser not supported');
      return;
    }

    console.log('Chrome Cast is available, loading SDK');
    loadCastSDK();

    // Cleanup function
    return () => {
      // Clean up button on unmount
      const buttonContainer = castButtonRef.current;
      if (buttonContainer) {
        buttonContainer.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeCastPlayer = () => {
    if (!window.cast || !window.cast.framework) {
      console.error('Cast framework is not available yet');
      return;
    }

    try {
      console.log('Initializing Cast Player...');

      const options = {
        receiverApplicationId: 'EB71DD7B',
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        androidReceiverCompatible: false,
        disableIdleTimeout: true
      };

      const context = window.cast.framework.CastContext.getInstance();
      context.setOptions(options);

      const currentSession = context.getCurrentSession();
      if (currentSession) {
        setCastSession(currentSession);
        setIsCastConnected(true);
        onCastStatusChange(true);
      }

      context.addEventListener(
        window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        event => {
          const isConnected = event.castState === window.cast.framework.CastState.CONNECTED;
          setIsCastConnected(isConnected);
          onCastStatusChange(isConnected);
          
          if (isConnected) {
            const session = context.getCurrentSession();
            setCastSession(session);
            
            // Send INIT_SESSION message to receiver
            if (session && sessionId) {
              const currentUser = auth.currentUser;
              const initMessage = {
                command: 'INIT_SESSION',
                sessionId: sessionId,
                userId: currentUser?.uid || 'anonymous'
              };
              
              console.log('Sending INIT_SESSION to receiver:', initMessage);
              session.sendMessage("urn:x-cast:com.fluffyware.pvideoke", initMessage)
                .then(() => {
                  console.log('INIT_SESSION sent successfully');
                })
                .catch(err => {
                  console.error('Error sending INIT_SESSION:', err);
                });
            }
            
            // Add message listener for receiver messages
            if (session) {
              session.addMessageListener("urn:x-cast:com.fluffyware.pvideoke", (namespace, message) => {
                console.log('Received message from receiver:', message);
                handleReceiverMessage(message);
              });
            }
          } else {
            setCastSession(null);
          }
        }
      );

    } catch (error) {
      console.error('Cast initialization failed:', error);
    }
  };

  // Handle messages from receiver
  const handleReceiverMessage = (message) => {
    try {
      const data = typeof message === 'string' ? JSON.parse(message) : message;
      console.log('Processed receiver message:', data);
      
      switch(data.command) {
        case 'SYNC_QUEUE':
          console.log('Queue sync from receiver:', data.param);
          // Notify parent component to update queue
          if (onCastStatusChange) {
            onCastStatusChange(true, { type: 'SYNC_QUEUE', queue: data.param });
          }
          break;
        case 'STATE_CHANGED':
          console.log('Receiver state changed:', data.state);
          break;
        case 'ERROR':
          console.error('Receiver error:', data.message);
          break;
        default:
          console.log('Unknown receiver command:', data.command);
      }
    } catch (error) {
      console.error('Error handling receiver message:', error);
    }
  };

  const sendCastMessage = (command, params = null) => {
    if (!castSession) {
      console.error('No active cast session');
      return Promise.reject('No active cast session');
    }

    // Standardize message structure
    const message = {
      command: command,
      params: params,
      timestamp: Date.now(),
      messageId: `${command}_${Date.now()}`
    };
    
    console.log('Sending cast message:', message);

    return castSession.sendMessage("urn:x-cast:com.fluffyware.pvideoke", message)
      .then(() => {
        console.log("Cast message sent successfully:", command);
        return true;
      })
      .catch(err => {
        console.error("Cast message error:", err);
        throw err;
      });
  };

  // Expose methods through a ref
  React.useImperativeHandle(ref, () => ({
    sendMessage: sendCastMessage,
    isConnected: () => isCastConnected,
    getSession: () => castSession
  }));

  return (
    <div className="cast-wrapper">
      <div ref={castButtonRef} id="cast-button-container"></div>
    </div>
  );
};

export default React.forwardRef(Cast); 